'use client';

import classNames from 'classnames';
import dynamic from 'next/dynamic';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import TrailDetailsShareOption from '@alltrails/amplitude/enums/TrailDetailsShareOption';
import TrailDetailsStickyNavSection from '@alltrails/amplitude/enums/TrailDetailsStickyNavSection';
import logTrailDetailsActionTapped from '@alltrails/amplitude/events/logTrailDetailsActionTapped';
import logTrailDetailsShareThisTrailOptionClicked from '@alltrails/amplitude/events/logTrailDetailsShareThisTrailOptionClicked';
import logTrailDetailsStickyNavClicked from '@alltrails/amplitude/events/logTrailDetailsStickyNavClicked';
import useMediaQuery from '@alltrails/core/hooks/useMediaQuery';
import type TrailDetails from '@alltrails/types/TrailDetails';
import IconButton from '@alltrails/denali/components/IconButton';
import TabBar from '@alltrails/denali/components/TabBar';
import Share from '@alltrails/denali/icons/Share';
import useIntersectionObserverCallback from '@alltrails/core/hooks/useIntersectionObserverCallback';
import { getTrailShareObject } from '@alltrails/trails';
import SaveCta from '@/components/SaveCta';
import { CONDITIONS_SECTION_ID, HTT_SECTION_ID, NEARBY_TRAILS_SECTION_ID, OVERVIEW_SECTION_ID, REVIEWS_SECTION_ID } from '@/utils/constants';
import { useAppDispatch, useAppSelector } from '@/utils/redux';
import SectionNavId from '@/utils/SectionNavId';
import { setActiveSection, setIsScrollingViaTabs } from '@/utils/trailPageSlice';
import DirectionsCta from '../DesktopCtas/DirectionsCta';
import CustomizeRouteButton from '../Overview/CustomizeRouteButton';
import styles from './DesktopNav.module.scss';

const ShareModal = dynamic(() => import('@alltrails/sharing/components/ShareModal/ShareModal'), { ssr: false });

function getAnalyticsNavSection(tabId: SectionNavId): TrailDetailsStickyNavSection {
  switch (tabId) {
    case OVERVIEW_SECTION_ID:
      return 'overview';
    case CONDITIONS_SECTION_ID:
      return 'conditions';
    case REVIEWS_SECTION_ID:
      return 'reviews';
    case HTT_SECTION_ID:
      return 'hit the trail';
    case NEARBY_TRAILS_SECTION_ID:
      return 'nearby';
    default:
      return 'overview';
  }
}

type DesktopNavProps = {
  trailDetails: TrailDetails;
  hasAdminToolbar: boolean;
};

const DesktopNav = ({ trailDetails, hasAdminToolbar }: DesktopNavProps) => {
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const stickyNavRef = useRef<HTMLDivElement>(null);
  const navRef = useRef<HTMLDivElement>(null);
  const ctasRef = useRef<HTMLDivElement>(null);
  const smallNav = useMediaQuery('(max-width: 960px)');
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const shareObject = useMemo(() => getTrailShareObject(trailDetails), [trailDetails]);
  const { id } = trailDetails;

  const activeSection = useAppSelector(state => state.trailPage.activeSection);

  const stickyObserver = useIntersectionObserverCallback(
    ([e]) => {
      const isSticking = e.intersectionRatio < 1;
      e.target.classList.toggle(styles.sticking, isSticking);
      navRef.current?.classList.toggle(styles.navSticking, isSticking);
      ctasRef.current?.classList.toggle(styles.ctasSticking, isSticking);
    },
    { threshold: 1 }
  );

  const onShareOptionClick = useCallback(
    (shareOption: TrailDetailsShareOption) => {
      logTrailDetailsShareThisTrailOptionClicked({ share_option_clicked: shareOption, trail_id: id.toString() });
    },
    [id]
  );

  const sectionLinks = useMemo(
    () => [
      { id: OVERVIEW_SECTION_ID, label: intl.formatMessage({ defaultMessage: 'Overview' }), testId: 'overview-link' },
      { id: CONDITIONS_SECTION_ID, label: intl.formatMessage({ defaultMessage: 'Conditions' }), testId: 'conditions-link' },
      { id: REVIEWS_SECTION_ID, label: intl.formatMessage({ defaultMessage: 'Reviews' }), testId: 'reviews-link' },
      ...(!smallNav
        ? [
            { id: HTT_SECTION_ID, label: intl.formatMessage({ defaultMessage: 'Hit the trail' }), testId: 'htt-link' },
            { id: NEARBY_TRAILS_SECTION_ID, label: intl.formatMessage({ defaultMessage: 'Nearby' }), testId: 'nearby-link' }
          ]
        : [])
    ],
    [intl, smallNav]
  );

  const onTabChange = useCallback(
    (tabId: string) => {
      logTrailDetailsStickyNavClicked({
        current_nav_section: getAnalyticsNavSection(activeSection),
        section_clicked: getAnalyticsNavSection(tabId as SectionNavId),
        trail_id: id
      });
      dispatch(setActiveSection(tabId as SectionNavId));
      dispatch(setIsScrollingViaTabs(true));

      window.addEventListener(
        'scrollend',
        () => {
          dispatch(setIsScrollingViaTabs(false));
        },
        { once: true }
      );
      document.getElementById(tabId)?.scrollIntoView({ behavior: 'smooth' });
    },
    [activeSection, dispatch, id]
  );

  const onShareClick = useCallback(() => {
    setIsShareModalOpen(true);
    logTrailDetailsActionTapped({
      trail_id: id.toString(),
      trail_page_action: 'share',
      trail_page_location: 'header bar'
    });
  }, [id]);

  useEffect(() => {
    const stickyNav = stickyNavRef.current;
    if (stickyNav && stickyObserver) {
      stickyObserver.observe(stickyNav);
    }

    return () => {
      stickyObserver?.disconnect();
    };
  }, [stickyObserver]);

  return (
    <>
      {isShareModalOpen && (
        <ShareModal isOpen onClose={() => setIsShareModalOpen(false)} onShareOptionClick={onShareOptionClick} shareObject={shareObject} />
      )}
      <div className={classNames(styles.stickyNavContainer, { [styles.hasAdminToolbar]: hasAdminToolbar })} ref={stickyNavRef}>
        <div className={styles.stickyNav} ref={navRef}>
          <TabBar
            activeTab={activeSection}
            className={styles.tabBar}
            hideBottomBorder
            indicatorPosition="top"
            onChange={onTabChange}
            tabs={sectionLinks}
          />
          <div className={styles.ctas} ref={ctasRef}>
            <CustomizeRouteButton trailSlug={trailDetails.slug} trailId={trailDetails.id} analyticsLocation="trail details navbar" isDesktopCta />
            <SaveCta buttonLocation="header bar" trailId={id} />
            <IconButton icon={{ Component: Share }} onClick={onShareClick} title="Share" />
            <DirectionsCta trailId={id} location={trailDetails.location} buttonLocation="header bar" />
          </div>
        </div>
      </div>
    </>
  );
};

export default DesktopNav;
